import { publicOrigin } from '@maxpreps/network/src/origins';
import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import React from 'react';
import { addGlobalQueryParams } from '../../functions/primitiveHelpers/urlHelpers';
import StyledAnchor, {
  IStyledAnchorProps
} from '../../ui/StyledAnchor/StyledAnchor';
import Link from '../Link/Link';

// if a link stharts with https:// or / then it looks like a link (absolute vs. relative)
// - we don't care about other protocols for now, at least in this context...
// Honestly this is mostly intended to filter out 'as' params in an element context
// - that accidently get propped through.
const looksLikeUrl = url =>
  url && (url.indexOf('https://') === 0 || url.indexOf('/') === 0);

const isCSRRoute = (href, as) => {
  if (!href) {
    return false;
  }

  /* We could try to make sure our pageMapping lines up with as but likely overkill
  and not applicable for projects that aren't using .htm pageMaps

  // If we don't have a mapping (via appRewrittenPages) then we can't be sure where it goes
  // we are going to fail internal (an 'as' was provided for a reason)
  if (!appRewrittenPages) {
    return true;
  }
  // if we have appRewrittenPages then 'as' must be .htm
  if (as.indexOf('.htm') === -1) {
    return false;
  }

  // we can't be certain its a links if we don't have all this info
  // we can get false positives here if there is a link to another app
  // that shares the same file name.
  const pageMap = getPageMapping(as, appRewrittenPages);

  return !pageMap || !!pageMap.unreleased;
  */

  // if we have both an href and an as, then we are clearly trying to create
  // an CSR <Link BUT there may be scenarios where an as gets through when it shouldn't (ex. as='div')
  return looksLikeUrl(as);
};

export interface IAnchorProps extends IStyledAnchorProps {
  isTrusted?: boolean;
  $underline?: boolean; // Design inconsistency - should be deprecated
}

/**
 * Smart wrapper for basic anchor. Props 'target' and 'rel' may be overwritten
 * @param isTrusted Indicates not to include 'noreferrer nofollow' in the rel
 */
const Anchor: React.FC<IAnchorProps> = ({
  href,
  as,
  isTrusted = false,
  children,
  target,
  rel,
  ...rest
}) => {
  const { appType } = useRequestContext();

  // Don't use Link if there is no href
  if (!href && rest.onClick) {
    return (
      // @ts-ignore
      <StyledAnchor as="button" type="button" {...rest}>
        {children}
      </StyledAnchor>
    );
  }

  const managedProps: React.HTMLProps<HTMLAnchorElement> = {
    href,
    target,
    rel
  };

  // Ensure external links are valid
  // Prevent one bad link from breaking an entire page
  let url: any = null;
  try {
    url = new URL(href as string, publicOrigin);
  } catch (e) {
    url = new URL(publicOrigin);
    console.error(e);
  }

  if (href && !url.origin.toLowerCase().endsWith('.maxpreps.com')) {
    managedProps.target = '_blank';
    if (isTrusted) {
      managedProps.rel = 'noopener';
    } else {
      managedProps.rel = 'noopener noreferrer nofollow';
    }
  }

  // Always add global query params
  managedProps.href = addGlobalQueryParams(managedProps.href, appType);

  const _isCSRRoute = isCSRRoute(href, as);

  // Don't use <Link> if it's a WF link.
  if (!_isCSRRoute) {
    return (
      // @ts-ignore
      <StyledAnchor {...rest} {...managedProps}>
        {children}
      </StyledAnchor>
    );
  }

  // (Ryan - 1/12/21) This comment is outdated but i want to keep it around for a few months in case we need to reevaluate
  // <Link> should only be for internal links but out isExternalRoute isn't perfect. It works, but if there is bad data being fed to GA when this happens
  // https://nextjs.org/docs/api-reference/next/link
  // https://stackoverflow.com/questions/61059111/external-link-is-not-working-in-next-js-when-you-want-to-use-link-component
  return (
    <Link as={as} href={href as string}>
      {/* @ts-ignore */}
      <StyledAnchor {...rest}>{children}</StyledAnchor>
    </Link>
  );
};

export default Anchor;
